import { create } from 'zustand';
import type { AssetGroup } from '@scio3/shared-types';
import type { SplitScreenAsset, SplitScreenZone } from './split-screen-dialog-store';

/**
 * Video-wall smallapp two-step flow state. Forked from
 * `split-screen-dialog-store.ts` per the Phase 2 spec recommendation
 * (separate stores so each migration owns its dialog state cleanly,
 * and the existing splitScreen path stays untouched).
 *
 * Both stores can never have a non-null `activeDialog` simultaneously
 * in practice — the App Selector closes before dispatching, and the
 * edit-asset path opens only one of them based on the asset's
 * `commonType` (`useOpenAssetEditor`). If they ever did overlap the
 * Suspense boundaries in `AppShell` would render both dialogs at once,
 * but the App Selector flow guarantees ordering.
 *
 * Mirrors scio1 `videoWallTemplatesModal` + `videoWallZonesModal` lifecycle.
 */
export type VideoWallDialogType = 'templates' | 'zones' | null;

/**
 * Video-wall asset shape — same as `SplitScreenAsset` plus a
 * `commonType: 'videoWall'` discriminator and a `meta` envelope that
 * carries the template's `templateConfig` / `templateId` /
 * `editorOrientation`. Re-exports the splitScreen zone type because
 * the per-zone shape is identical (id / name / position / content /
 * scale / duration / etc.).
 */
export interface VideoWallAsset extends SplitScreenAsset {
  /** Always `'videoWall'` at runtime — kept widened to `string` so
   *  shared helpers like `normalizeSplitScreenForSave` (which return
   *  `SplitScreenAsset` with `commonType?: string`) round-trip cleanly. */
  commonType?: string;
  meta?: {
    templateConfig?: string;
    editorOrientation?: string;
    templateId?: string;
    zoneNumber?: number;
    [key: string]: unknown;
  };
}

export type VideoWallZone = SplitScreenZone;

/**
 * The picker can be opened in two modes:
 *
 * - `asset-create` (default) — the Templates dialog → Zones dialog
 *   flow used when creating a new Video Wall asset from the App
 *   Selector. Picking a template transitions to the Zones editor.
 *
 * - `layout-select` — the Templates dialog only. Used by the Edit
 *   Screen dialog's Video Wall section "Change" button to pick a
 *   layout for the device's `videoWallLayout` field. Picking a tile
 *   resolves the `onLayoutSelected` callback with the template's
 *   `templateConfig` + `orientation` and closes the dialog — does
 *   NOT proceed to the Zones editor (there's no asset to edit).
 */
export type VideoWallPickMode = 'asset-create' | 'layout-select';

interface VideoWallDialogContext {
  currentPath?: string;
  currentTeamId?: string;
  currentGroup?: AssetGroup | null;
  mode?: 'add' | 'edit';
  asset?: VideoWallAsset;
  pickMode?: VideoWallPickMode;
  onLayoutSelected?: (result: { templateConfig: string; orientation: string }) => void;
  /**
   * Fires after the Zones dialog's saveAsset succeeds (asset-create flow
   * only). Used by callers like `AddOnConfigDialog` that need the newly
   * created asset's id/name so they can populate their own form (e.g.
   * Screens → +Add On → Video Wall → Change → create asset → return
   * `{_id, originalFileName}` to the Content field).
   */
  onAssetCreated?: (asset: { _id: string; originalFileName?: string }) => void;
}

interface VideoWallDialogState {
  activeDialog: VideoWallDialogType;
  dialogContext: VideoWallDialogContext;

  /** Opened from `useOpenAppModal.processApp` when user clicks "VideoWall Content".
   *  `onAssetCreated` is invoked after the Zones dialog saveAsset succeeds — set
   *  by callers that need the new asset back (e.g. AddOnConfigDialog Content
   *  picker). When omitted, the dialog simply closes after save. */
  openTemplates: (ctx: {
    currentPath?: string;
    currentTeamId?: string;
    currentGroup?: AssetGroup | null;
    onAssetCreated?: (asset: { _id: string; originalFileName?: string }) => void;
  }) => void;

  /** Called from the Templates dialog after the user picks a layout — transitions to Zones with a fresh asset. */
  openZonesFromTemplate: (asset: VideoWallAsset) => void;

  /** Opened from `useOpenAssetEditor` for an existing video-wall asset (edit mode). */
  openZones: (args: {
    mode: 'add' | 'edit';
    asset: VideoWallAsset;
    currentPath?: string;
    currentTeamId?: string;
  }) => void;

  /**
   * Open the Templates dialog in `layout-select` mode. The Edit Screen
   * dialog's Video Wall section calls this from its "Change" button to
   * let the user pick a layout for the device. Picking a tile resolves
   * `onSelect` with the chosen template's `templateConfig` + `orientation`
   * and closes the dialog (does NOT proceed to Zones — there's no asset).
   */
  openTemplatesForLayout: (
    onSelect: (result: { templateConfig: string; orientation: string }) => void,
  ) => void;

  closeDialog: () => void;
}

export const useVideoWallDialogStore = create<VideoWallDialogState>((set) => ({
  activeDialog: null,
  dialogContext: {},

  openTemplates: ({ currentPath, currentTeamId, currentGroup, onAssetCreated }) =>
    set({
      activeDialog: 'templates',
      dialogContext: {
        currentPath,
        currentTeamId,
        currentGroup,
        mode: 'add',
        pickMode: 'asset-create',
        onAssetCreated,
      },
    }),

  openTemplatesForLayout: (onSelect) =>
    set({
      activeDialog: 'templates',
      dialogContext: { pickMode: 'layout-select', onLayoutSelected: onSelect },
    }),

  openZonesFromTemplate: (asset) =>
    set((state) => ({
      activeDialog: 'zones',
      dialogContext: {
        ...state.dialogContext,
        mode: 'add',
        asset,
      },
    })),

  openZones: ({ mode, asset, currentPath, currentTeamId }) =>
    set({
      activeDialog: 'zones',
      dialogContext: { mode, asset, currentPath, currentTeamId },
    }),

  closeDialog: () => set({ activeDialog: null, dialogContext: {} }),
}));
